import { ApplicationElement, html } from '../application_element/element.js';
import CommonActions from '../common_actions/element.js';
import debounce from 'lodash-es/debounce.js';
import '../badge/element.js';
import '../label/element.js';
import '../icon/element.js';
import '../tooltip/element.js';

import translations from './translations.js';
import style from './style.lit.css';

/**
 * One item in organize card
 *
 * @prop itemId - Id of the item
 * @prop cardId - Id of the card
 * @prop amount - Current amount of helpers
 * @prop maxAvatarsLength - Maximum number of shown avatars (optional, default: 5)
 * @prop goal - Goal amount of helpers (optional)
 * @prop amountContributed - Amount of help the current user contributed in customAmount items
 * @prop shouldShowBadge - Should show badge with amountContributed?
 * @prop checked - Is current user helping? (optional)
 * @prop disabled - Should disable action? (optional)
 * @prop customAmount - Can amount of help offered be custom? (optional)
 * @prop layout - Layout of item (options: row, column)
 *
 * @cssprop [--count-color: var(--dark-text-secondary)] Count label text color
 * @cssprop [--count-font-size: var(--font-size-sm)] Count label text size
 * @cssprop [--count-padding: 4px] Count label padding
 * @cssprop [--count-margin: 0 16px 0 0] Count label margin
 * @cssprop [--count-background: var(--contrast-light)] Count label background
 * @cssprop [--count-label-display: none] Display of count "Helpers" label (recommended: none | inline)
 * @cssprop [--button-color: var(--dark-text)] Action button text color--count-color: var(--dark-text-secondary);
 * @cssprop [--button-color-hover: var(--primary)] Action button text color on hover
 * @cssprop [--button-color-active: var(--dark-text-secondary)] Action button text color on active
 * @cssprop [--button-margin: 0 0 0 16px] Action button margin
 * @cssprop [--button-icon-size: 24px] Action button text icon size
 * @cssprop [--badge-color: var(--secondary)] Background color of amount-contributed badge
 *
 * @slot default - Title/label
 * @slot avatars - Avatar list of helpers
 */

export default class OrganizeOption extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    itemId: { type: Number },
    cardId: { type: Number },
    amount: { type: Number },
    maxAvatarsLength: { type: Number },
    amountContributed: { type: Number },
    goal: { type: Number },
    checked: { type: Boolean },
    disabled: { type: Boolean },
    customAmount: { type: Boolean },
    layout: { type: String, reflect: true },
    shouldShowBadge: { type: Boolean },
    users: { type: Array, state: true },
    preventInput: { type: Boolean, state: true },
  };

  static config = {
    icons: {
      plus: 'plus-circle',
      minus: 'minus-circle',
      check: 'check',
    },
  };

  constructor() {
    super();
    this.commonActions = new CommonActions(this);
    this.amount = 0;
    this.layout = 'column';
    this.users = [];
    this.maxAvatarsLength = 5;
    this.amountContributed = 0;
    this.debouncedHandleResize = debounce(() => this.handleResize(), 50);
  }

  connectedCallback() {
    super.connectedCallback();

    this.initLayout = this.layout;
    this.handleResize();
    window.addEventListener('resize', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this);
  }

  /** @internal */
  get icon() {
    return this.constructor.config.icons[this.checked ? 'minus' : 'plus'];
  }

  /** @internal */
  get baseUrl() {
    return `/cards/${this.cardId}/items/${this.itemId}/assigns`;
  }

  /** @internal */
  get checkCustomUrl() {
    return `${this.baseUrl}/new`;
  }

  /** @internal */
  get uncheckUrl() {
    return `${this.baseUrl}/${this.cardId}`;
  }

  /** @internal */
  get detailsUrl() {
    return `/organize_cards/${this.cardId}/assigns?item=${this.itemId}`;
  }

  handleEvent(e) {
    e.type === 'resize' && this.debouncedHandleResize();
  }

  handleResize() {
    if (window.innerWidth < 768) {
      this.layout = 'row';
    } else {
      this.layout = this.initLayout;
    }
  }

  dispatchChangeEvent() {
    this.dispatchEvent(
      new Event('organizeOption-change', {
        bubbles: true,
        composed: true,
      })
    );
  }

  check(amount = 1) {
    this.checked = true;
    this.users = [this.AppConfig.user, ...this.users];

    if (this.avatarList) {
      this.avatarList.users = this.users.slice(0, this.maxAvatarsLength);
      this.avatarList.totalCount++;
    }

    this.amount += amount;
    this.amountContributed = amount;

    this.dispatchChangeEvent();
    this.commonActions.displaySnackBarSuccess(this.t('snack.check_success'));
  }

  uncheck(amount = this.amountContributed || 1) {
    this.checked = false;
    this.users = this.users.filter(a => a.id !== this.AppConfig.user.id);

    if (this.avatarList) {
      this.avatarList.users = this.users.slice(0, this.maxAvatarsLength);
      this.avatarList.totalCount--;
    }

    this.amount -= amount;
    this.amountContributed = 0;

    this.dispatchChangeEvent();
    this.commonActions.displaySnackBarSuccess(this.t('snack.uncheck_success'));
  }

  async handleClick() {
    if (this.preventInput || this.disabled) return;
    this.preventInput = true;

    try {
      if (!this.checked && !this.customAmount) {
        this.check();
        await this.commonActions.post(this.baseUrl);
      } else if (this.checked) {
        this.uncheck();
        await this.commonActions.delete(this.uncheckUrl);
      } else await this.commonActions.execJSFromResponse(this.checkCustomUrl);
    } catch {
      this.checked ? this.uncheck() : this.check();
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    } finally {
      this.preventInput = false;
    }
  }

  handleAvatarSlotChange(e) {
    this.avatarList = e.target
      .assignedElements({ flatten: true })
      .find(e => e.tagName === 'EF-AVATAR-LIST');

    if (this.avatarList) {
      this.avatarList.url ||= this.detailsUrl;
      this.users = this.avatarList.users;
      this.avatarList.users = this.users.slice(0, this.maxAvatarsLength);
    }
  }

  renderLabel() {
    return html`
      <ef-label>
        ${this.goal ? `${this.amount}/${this.goal}` : this.amount}
        <span class="count-label">
          ${this.t('helpers_count', {
            count: this.amount,
          })}
        </span>
      </ef-label>
    `;
  }

  renderAction() {
    return html`
      <div class="action-wrapper">
        ${this.shouldShowBadge ? this.renderBadge() : null}
        <button @click=${this.handleClick}>
          <ef-icon .name=${this.icon}></ef-icon>
        </button>
      </div>
    `;
  }

  renderCheckedIcon() {
    return html`
      <div class="action-wrapper">
        ${this.shouldShowBadge ? this.renderBadge() : null}
        <ef-icon name=${this.constructor.config.icons.check}></ef-icon>
      </div>
    `;
  }

  renderBadge() {
    const numberTooBig = this.amountContributed > 999;
    const count = numberTooBig ? '999+' : this.amountContributed;
    const badgeElement = html`<ef-badge .count=${count}></ef-badge>`;

    return numberTooBig
      ? html`
          <ef-tooltip hoist .body=${this.amountContributed}>
            ${badgeElement}
          </ef-tooltip>
        `
      : badgeElement;
  }

  renderActionWrapper() {
    return html`
      ${!this.disabled ? this.renderAction() : null}
      ${this.disabled && this.checked ? this.renderCheckedIcon() : null}
    `;
  }

  renderAvatars() {
    return html`
      <slot @slotchange=${this.handleAvatarSlotChange} name="avatars"></slot>
    `;
  }

  renderColumnLayout() {
    return html`
      <div class="left">
        <slot></slot>
        ${this.renderLabel()}
      </div>
      <div class="right">
        ${this.renderAvatars()} ${this.renderActionWrapper()}
      </div>
    `;
  }

  renderRowLayout() {
    return html`
      <div class="top">
        <slot></slot>
      </div>
      <div class="bottom">
        <div class="left">${this.renderLabel()} ${this.renderAvatars()}</div>
        <div class="right">${this.renderActionWrapper()}</div>
      </div>
    `;
  }

  render() {
    return this.layout === 'row'
      ? this.renderRowLayout()
      : this.renderColumnLayout();
  }
}

OrganizeOption.registerTagName('ef-organize-option');

customElements.get('ef-organize-option') ||
  customElements.define('ef-organize-option', OrganizeOption);
